// Heimdall — the AI analyst layer (Heimdall brief).
// Pure, deterministic helpers for a simulated analyst: label maps, a canned finding
// library, review-state derivation and counters. No real model runs anywhere; the
// same input always yields the same findings (Heimdall brief §2, simulation rules).
//
// One-way import discipline: this module is imported by mutations / screens, and it
// imports only types — never hooks or the store — so there are no cycles.

import type {
  AIFinding,
  AIFindingCategory,
  AIFindingSeverity,
  AIReview,
  AIReviewState,
  SpendRequest,
  WorkspaceSeed,
} from "./types";

/** The analyst's default name. Workspaces may rename it via terminology.analyst. */
export const ANALYST_NAME = "Heimdall";

export const FINDING_CATEGORY_LABEL: Record<AIFindingCategory, string> = {
  integrity: "Integrity",
  plausibility: "Plausibility",
  completeness: "Completeness",
  compliance: "Compliance",
  progress: "Progress",
};

export const FINDING_SEVERITY_LABEL: Record<AIFindingSeverity, string> = {
  info: "Note",
  attention: "Attention",
  serious: "Serious",
};

/** Severity → status-token family (maps onto the design system's ok/warn/bad/info). */
export const FINDING_SEVERITY_TONE: Record<AIFindingSeverity, "info" | "warn" | "bad"> = {
  info: "info",
  attention: "warn",
  serious: "bad",
};

export const REVIEW_STATE_LABEL: Record<AIReviewState, string> = {
  pending: "Not yet reviewed",
  passed: "Checks passed",
  flagged: "Flags raised",
};

// ---------------------------------------------------------------------------
// Review-state derivation
// ---------------------------------------------------------------------------

/**
 * The review state for a subject (ask or evidence), derived from its findings. An explicit
 * stored review wins (e.g. a just-submitted item staged as "pending"); otherwise the state
 * falls out of whether any open or accepted findings cite it. Dismissed findings don't flag.
 */
export function reviewStateFromFindings(stored: AIReview | undefined, findings: AIFinding[]): AIReviewState {
  if (stored?.state === "pending") return "pending";
  const live = findings.filter((f) => f.status !== "dismissed");
  return live.length > 0 ? "flagged" : "passed";
}

/** All findings attached to a given spend ask. */
export function findingsForAsk(findings: AIFinding[], askId: string): AIFinding[] {
  return findings.filter((f) => f.subjectAskId === askId);
}

/** All findings attached to a given evidence item. */
export function findingsForEvidence(findings: AIFinding[], evidenceId: string): AIFinding[] {
  return findings.filter((f) => f.subjectEvidenceId === evidenceId);
}

/** The review badge for an ask, combining any stored state with its findings. */
export function reviewForAsk(ask: SpendRequest, findings: AIFinding[]): AIReview {
  const own = findingsForAsk(findings, ask.id);
  return {
    state: reviewStateFromFindings(ask.aiReview, own),
    findingIds: own.map((f) => f.id),
    reviewedAt: ask.aiReview?.reviewedAt,
  };
}

// ---------------------------------------------------------------------------
// Counters (for the funder verification summary and the findings queue)
// ---------------------------------------------------------------------------

export type FindingStats = {
  total: number;
  open: number;
  accepted: number;
  dismissed: number;
  serious: number;
};

export function findingStats(findings: AIFinding[]): FindingStats {
  return {
    total: findings.length,
    open: findings.filter((f) => f.status === "open").length,
    accepted: findings.filter((f) => f.status === "accepted").length,
    dismissed: findings.filter((f) => f.status === "dismissed").length,
    serious: findings.filter((f) => f.severity === "serious" && f.status !== "dismissed").length,
  };
}

export type ProgrammeReviewStats = {
  reviewed: number;
  flagged: number;
  passRate: number; // 0..100
  avgDecisionHours: number | null;
};

/**
 * Single source of truth for the programme-level review numbers shown in both the findings
 * queue and the analytics panel, so the two surfaces can never disagree (a house-rule
 * guardrail: no number differs across surfaces).
 */
export function programmeReviewStats(seed: WorkspaceSeed): ProgrammeReviewStats {
  const findings = seed.aiFindings ?? [];
  const live = findings.filter((f) => f.status !== "dismissed");
  const reviewed = seed.spendRequests.length;
  const flaggedAskIds = new Set(live.filter((f) => f.subjectAskId).map((f) => f.subjectAskId));
  const passRate = reviewed ? Math.round(((reviewed - flaggedAskIds.size) / reviewed) * 100) : 100;
  const decided = findings.filter((f) => f.decidedAt);
  const avgDecisionHours = decided.length
    ? Math.round(
        decided.reduce((a, f) => a + (new Date(f.decidedAt!).getTime() - new Date(f.createdAt).getTime()) / 3.6e6, 0) / decided.length,
      )
    : null;
  return { reviewed, flagged: flaggedAskIds.size, passRate, avgDecisionHours };
}

// ---------------------------------------------------------------------------
// Verification summary — the assurance Heimdall + a human jointly give a milestone.
// Used on the funder disbursement view and the oversight routing drawer (Heimdall brief §4).
// Always pairs AI-reviewed with human-confirmed; never presents AI review alone.
// ---------------------------------------------------------------------------

export type VerificationSummary = {
  items: number;
  passed: number;
  flagsRaised: number;
  flagsResolved: number;
  allConfirmed: boolean;
};

export function verificationSummary(seed: WorkspaceSeed, milestoneId: string): VerificationSummary {
  const asks = seed.spendRequests.filter((r) => r.milestoneId === milestoneId && r.state !== "rejected");
  const askIds = new Set(asks.map((r) => r.id));
  const evidence = (seed.evidence ?? []).filter((e) => askIds.has(e.linkedAskId));
  const findings = (seed.aiFindings ?? []).filter((f) => f.subjectAskId && askIds.has(f.subjectAskId));
  const flagsRaised = findings.length;
  const flagsResolved = findings.filter((f) => f.status !== "open").length;
  const items = evidence.length;
  const moneyOut = asks.filter((r) => r.state === "committed" || r.state === "evidence_submitted" || r.state === "confirmed");
  const allConfirmed = moneyOut.every((r) => r.state === "confirmed") && flagsRaised === flagsResolved;
  return { items, passed: Math.max(0, items - flagsRaised), flagsRaised, flagsResolved, allConfirmed };
}

// ---------------------------------------------------------------------------
// Canned finding library — templates Heimdall draws on when reviewing a live
// submission in the demo (Heimdall brief §2, simulation rules). Deterministic.
// ---------------------------------------------------------------------------

export type CannedFinding = Omit<AIFinding, "id" | "createdAt" | "status" | "citations"> & {
  /** A hint for which subject this template typically attaches to. */
  defaultCategory: AIFindingCategory;
};

export const CANNED_FINDINGS: Record<string, Omit<AIFinding, "id" | "createdAt" | "status" | "citations">> = {
  reused_photo: {
    severity: "serious",
    category: "integrity",
    statement:
      "This photo matches one already submitted on a different ask. The same image appears to have been reused as evidence for separate spend.",
    confidence: "high",
  },
  duplicate_invoice: {
    severity: "serious",
    category: "integrity",
    statement:
      "This invoice closely matches an order already committed on another ask · same supplier, near-identical amount and timing. It may be a double-spend.",
    confidence: "high",
  },
  future_dated: {
    severity: "attention",
    category: "integrity",
    statement: "The document date is later than the claimed work date, which is inconsistent.",
    confidence: "moderate",
  },
  missing_photo: {
    severity: "attention",
    category: "completeness",
    statement: "A line item on this ask has no photo or receipt attached.",
    confidence: "high",
  },
  missing_geo: {
    severity: "info",
    category: "completeness",
    statement: "A captured photo has no location tag, so it cannot be placed at the site.",
    confidence: "moderate",
  },
  amount_outlier: {
    severity: "attention",
    category: "plausibility",
    statement: "This amount is well above similar line items on comparable sites for the same category.",
    confidence: "moderate",
  },
  kyc_stale: {
    severity: "attention",
    category: "compliance",
    statement: "The supplier's tax clearance is older than 12 months, so its KYC record is stale.",
    confidence: "high",
  },
  near_threshold: {
    severity: "attention",
    category: "compliance",
    statement: "This ask sits just below the compliance threshold; combined with a recent ask it may indicate structuring.",
    confidence: "moderate",
  },
};

/**
 * Heimdall's canned verdict for a just-submitted ask (the staged live review). Deterministic
 * by the ask's shape so the demo is repeatable: an ask whose title hints at a reused photo or a
 * duplicate flags; everything else passes cleanly. The real product would run a model here.
 */
export function cannedReviewForAsk(ask: SpendRequest): { state: AIReviewState; cannedKey?: keyof typeof CANNED_FINDINGS } {
  const t = `${ask.title} ${ask.purpose}`.toLowerCase();
  if (t.includes("welding") || t.includes("duplicate")) return { state: "flagged", cannedKey: "duplicate_invoice" };
  if (t.includes("reuse") || t.includes("same photo")) return { state: "flagged", cannedKey: "reused_photo" };
  return { state: "passed" };
}
